import React from 'react';
import styled from 'styled-components';

import { colors } from 'constants/colors';

import { dateFromString, formatDateForDisplay } from 'helpers/dateTimes';

import { GetMicrostoreProductsQuery, RemoveProductsInstructionFragmentFragment } from 'data/__generated__';

export const RemovedProductsSection = ({
    removedProducts,
    inventoryDate,
    products,
    setSelectedLocationId,
    selectedLocationId,
}: {
    removedProducts: RemoveProductsInstructionFragmentFragment['removedProducts'];
    inventoryDate: string;
    products: GetMicrostoreProductsQuery['getMicrostoreProducts'];
    setSelectedLocationId?: (selectedLocationId: string | null) => void;
    selectedLocationId?: string | null;
}) => {
    const removedProductIds = removedProducts.map(({ productId }) => productId);
    const removedProductsFromCatalog = products.filter((product) => removedProductIds.includes(product._id));

    const startOfInventoryDay = dateFromString(inventoryDate);
    startOfInventoryDay.setHours(0, 0, 0, 0);

    // Find which product corresponds to the selected location
    const selectedProductIdFromLocation = React.useMemo(() => {
        if (!selectedLocationId) return null;
        const removedProduct = removedProducts.find((rp) => {
            return rp.microstoreLocationIds.includes(selectedLocationId);
        });
        return removedProduct?.productId ?? null;
    }, [selectedLocationId, removedProducts]);

    return (
        <ProductsContainer>
            {removedProductsFromCatalog.map((removedProductFromCatalog) => {
                const associatedRemovedProducts = removedProducts.filter(
                    ({ productId }) => productId === removedProductFromCatalog._id,
                );
                const isSelected =
                    selectedLocationId === removedProductFromCatalog._id ||
                    selectedProductIdFromLocation === removedProductFromCatalog._id;
                return (
                    <ProductContainer key={removedProductFromCatalog._id}>
                        <ProductName
                            onClick={() => {
                                const firstLocationId =
                                    associatedRemovedProducts.map((rp) => rp.microstoreLocationIds || []).flat()[0] ??
                                    null;
                                setSelectedLocationId?.(firstLocationId);
                            }}
                            $isSelected={isSelected}
                        >
                            {removedProductFromCatalog.name}
                        </ProductName>
                        {/* Todo: in a future developement, the removed products will be associated to a location */}
                        <LocationsContainer>
                            {associatedRemovedProducts.map((removedProduct) => {
                                const hasSingleLocation = removedProduct.microstoreLocationIds.length <= 1;
                                return removedProduct.microstoreLocationIds.map((locationId, locationIndex) => {
                                    const isFirstLocation = locationIndex === 0;
                                    return (
                                        <LocationContainer key={locationId}>
                                            <LocationName>Emplacement {locationIndex + 1}</LocationName>
                                            <QuantitiesContainer>
                                                {!isFirstLocation || !removedProduct.quantityDetails.length ? (
                                                    <QuantityContainer>
                                                        {hasSingleLocation ? 'Vide' : '-'}
                                                    </QuantityContainer>
                                                ) : (
                                                    removedProduct.quantityDetails.map(({ expiryDate, quantity }) => {
                                                        const isExpired = !!(
                                                            expiryDate &&
                                                            dateFromString(expiryDate) < startOfInventoryDay
                                                        );
                                                        return (
                                                            <QuantityContainer key={expiryDate} $isExpired={isExpired}>
                                                                Retiré: Qty: {quantity} ; DLC:{' '}
                                                                {expiryDate
                                                                    ? formatDateForDisplay(expiryDate)
                                                                    : 'Non Rensigné'}
                                                            </QuantityContainer>
                                                        );
                                                    })
                                                )}
                                            </QuantitiesContainer>
                                        </LocationContainer>
                                    );
                                });
                            })}
                        </LocationsContainer>
                    </ProductContainer>
                );
            })}
        </ProductsContainer>
    );
};

const ProductsContainer = styled.div`
    flex-grow: 1;
    & > :not(:first-child) {
        border-top: 1px solid ${colors.lightGrey};
        margin-top: 5px;
    }
`;
const ProductContainer = styled.div`
    display: flex;
    justify-content: space-between;
`;
const ProductName = styled.div<{ $isSelected?: boolean }>`
    cursor: pointer;
    font-weight: bold;
    width: 50%;
    color: ${({ $isSelected }) => ($isSelected ? colors.greenDarker : colors.pureBlack)};
`;
const LocationsContainer = styled.div`
    margin-left: 10px;
    width: 50%;
`;
const LocationContainer = styled.div`
    display: flex;
`;
const LocationName = styled.div`
    font-style: italic;
`;
const QuantitiesContainer = styled.div``;
const QuantityContainer = styled.div<{ $isExpired?: boolean }>`
    margin-left: 10px;
    color: ${({ $isExpired }) => ($isExpired ? colors.red : colors.pureBlack)};
`;
